import { useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";


/**
 * OLD VERSION OF HOOK
 * use the latest version useAnalyticsV2.ts
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface AnalyticsConfig {
  sendPageView?: boolean;
}

const useAnalytics = (userId?: string, config?: AnalyticsConfig) => {
  const isInitialized = useRef(false);

  const initAnalytics = useCallback(
    (tag: string, initConfig?: AnalyticsConfig) => {
      return new Promise<void>((resolve, reject) => {
        if (isInitialized.current) {
          console.warn('Google Analytics is already initialized.');
          return resolve();
        }

        const mergedConfig = {
          sendPageView: false,
          ...config,
          ...initConfig
        };

        try {
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${tag}`;
          script.onerror = (error) => {
            console.error('Failed to load Google Analytics script:', error);
            reject(new Error('GA script failed to load.'));
          };
          document.head.appendChild(script);

          window.dataLayer = window.dataLayer || [];
          // eslint-disable-next-line
          function gtag() {
            window.dataLayer.push(arguments);
          }
          window.gtag = gtag;

          script.onload = () => {
            try {
              window.gtag('js', new Date());
              
              const gaConfig: Record<string, any> = {
                send_page_view: mergedConfig.sendPageView
              };
              
              if (userId) {
                gaConfig.user_id = userId;
              }
              
              window.gtag('config', tag, gaConfig);

              isInitialized.current = true;
              resolve();
            } catch (error) {
              console.error('GA script loaded, but initialization failed:', error);
              reject(error);
            }
          };
        } catch (error) {
          console.error('Unexpected error while initializing Google Analytics:', error);
          reject(error);
        }
      });
    },
    [isInitialized, userId, config]
  );

  const sendEvent = useCallback(
    (eventName: string, eventParams: { [key: string]: any } = {}) => {
      if (!isInitialized.current) {
        console.warn('Google Analytics is not initialized. Call initAnalytics() first.');
        return;
      }

      try {
        if (userId) {
          eventParams['user_id'] = userId;
        }
        
        if (typeof window.gtag !== 'function') {
          throw new Error('gtag function is not available.');
        }
        window.gtag('event', eventName, eventParams);
      } catch (error) {
        console.error(`Failed to send GA event "${eventName}":`, error);
        Sentry.captureException(new Error(`GA event failure: ${eventName} - ${error.message}`));
      }
    },
    [isInitialized, userId, config]
  );

  return { initAnalytics, sendEvent };
};

export default useAnalytics;

